import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { constants as damagesConstants } from '../../redux/damages';

import './sass/damage-visualizer.scss';

const LINE_COLOR = 'rgba(255,0,0,0.4)';
const FILL_COLOR = LINE_COLOR;
const LINE_WIDTH = 2;

const drawBox = (canvas, box) => {
  if (!box || typeof box === 'undefined') return null;

  const ctx = canvas.getContext('2d');
  const coord = box.coord ? box.coord : box;

  let [x, y, width, height] = [0, 0, 0, 0];

  if (typeof coord.xmin !== 'undefined'
      && typeof coord.xmax !== 'undefined'
      && typeof coord.ymin !== 'undefined'
      && typeof coord.ymax !== 'undefined')
  {
    [x, y, width, height] = [
      Math.min(coord.xmin, coord.xmax),
      Math.min(coord.ymin, coord.ymax),
      Math.max(coord.xmin, coord.xmax) - Math.min(coord.xmin, coord.xmax),
      Math.max(coord.ymin, coord.ymax) - Math.min(coord.ymin, coord.ymax),
    ];
  } else {
    [x, y, width, height] = coord;
  }

  if (x < LINE_WIDTH / 2) x = LINE_WIDTH / 2;
  if (y < LINE_WIDTH / 2) y = LINE_WIDTH / 2;

  if (x + width > canvas.width) {
    width = canvas.width - LINE_WIDTH - x;
  }
  if (y + height > canvas.height) {
    height = canvas.height - LINE_WIDTH - y;
  }

  ctx.strokeStyle = LINE_COLOR;
  ctx.lineWidth = LINE_WIDTH;
  ctx.fillStyle = FILL_COLOR;

  // Draw the entire box in one path
  ctx.beginPath();
  ctx.rect(x, y, width, height);

  // Fill first
  ctx.fill();

  // Then stroke
  ctx.stroke();
};

const drawPolygon = (canvas, points) => {
  if (!points || points.length < 2) return;

  const ctx = canvas.getContext('2d');

  ctx.strokeStyle = LINE_COLOR;
  ctx.lineWidth = LINE_WIDTH;

  // Start a new path
  ctx.beginPath();

  // Move to the first point
  ctx.moveTo(points[0].x, points[0].y);

  // Draw lines to subsequent points
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }

  // Close the polygon by connecting back to first point
  ctx.lineTo(points[0].x, points[0].y);
  ctx.closePath();

  // Fill the polygon
  ctx.fillStyle = FILL_COLOR;
  ctx.fill();

  // Stroke the path
  ctx.stroke();
};

const calculateBoundingBox = (points) => {
  const xCoordinates = points.map((point) => point.x);
  const yCoordinates = points.map((point) => point.y);

  const xMin = Math.min(...xCoordinates);
  const yMin = Math.min(...yCoordinates);

  const xMax = Math.max(...xCoordinates);
  const yMax = Math.max(...yCoordinates);

  return [xMin, yMin, xMax - xMin, yMax - yMin];
};

const DamageVisualizer = ({ imageSrc, damages }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');

    const handleImageLoad = () => {
      // Set canvas dimensions to match image
      canvas.width = image.width;
      canvas.height = image.height;

      // Draw image
      ctx.drawImage(image, 0, 0);

      damages.forEach(({ damageType, location }) => {
        location.forEach((loc) => {
          if (damageType !== damagesConstants.DAMAGE_TYPE.CRACK) {
            drawBox(canvas, calculateBoundingBox(loc));
          } else {
            drawPolygon(canvas, loc);
          }
        });
      });
    };

    // Check if image is already loaded
    if (image.complete && image.naturalHeight !== 0) {
      handleImageLoad();
    } else {
      image.onload = handleImageLoad;
    }
  }, [imageSrc, damages]);

  if (!damages || damages.length === 0) {
    return <img src={imageSrc} alt="Damage visualizer" />;
  }

  return (
    <div className="damage-visualizer-wrapper">
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Damage visualizer"
        style={{ display: 'none' }}
      />
      <canvas
        ref={canvasRef}
        style={{
          maxHeight: '100vh',
          maxWidth: '100%'
        }}
      />
    </div>
  );
};

DamageVisualizer.propTypes = {
  damages: PropTypes.array,
  imageSrc: PropTypes.string,
};

export default DamageVisualizer;
